import { useCallback, useEffect, useRef, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { motion, AnimatePresence } from "framer-motion";
import { NavigationArrow, X, ArrowsClockwise, GearSix } from "@phosphor-icons/react";
import VerdictPage from "@/pages/v2/VerdictPage";
import TodayPage from "@/pages/v2/TodayPage";
import PulsePage from "@/pages/v2/PulsePage";
import NearbyRainPage from "@/pages/v2/NearbyRainPage";
import ReportPage from "@/pages/v2/ReportPage";
import ExtremesPage from "@/pages/v2/ExtremesPage";
import FloodRiskPage from "@/pages/v2/FloodRiskPage";
import useLiveFeed from "@/hooks/useLiveFeed";
import useRainConfidence from "@/hooks/useRainConfidence";
import { getTheme } from "@/lib/weatherTheme";
import { reverseGeocode } from "@/lib/api";
import InstallPwaPrompt from "@/components/InstallPwaPrompt";
import SwipePager from "@/components/v2/SwipePager";
import SettingsSheet from "@/components/v2/SettingsSheet";
import AccountChip from "@/components/v2/AccountChip";
import ProGate from "@/components/v2/ProGate";
import TrialExpiredBanner from "@/components/v2/TrialExpiredBanner";
import { useSettings } from "@/lib/settings";
import { vibrate, HAPTICS } from "@/lib/haptics";

const DEFAULT_LOCATION = {
  name: "Hyderabad",
  admin1: "Telangana",
  country: "India",
  latitude: 17.385,
  longitude: 78.4867,
};

const LS_KEY = "nowcast.lastLocation.v1";
const LS_GEO_ASKED = "nowcast.geolocationAsked.v1";

// Coordinate-shaped names like "8.714, 77.757" — these were stored by the
// pre-fix reverse-geocoder. Detect them so we can re-fetch a real place name.
const COORD_NAME_RE = /^-?\d+\.\d+,\s*-?\d+\.\d+$/;

function loadStoredLocation() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (typeof p?.latitude === "number" && typeof p?.longitude === "number") return p;
  } catch { /* noop */ }
  return null;
}

function isFirstVisit() {
  // No stored location AND no record of a prior permission prompt = brand-new user
  try {
    return !localStorage.getItem(LS_KEY) && !localStorage.getItem(LS_GEO_ASKED);
  } catch {
    return false;
  }
}

export default function Home() {
  const stored = loadStoredLocation();
  const [location, setLocation] = useState(stored || DEFAULT_LOCATION);
  const [activePage, setActivePage] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  // Transient hint shown after geolocation is denied/dismissed on first visit
  const [showGpsHint, setShowGpsHint] = useState(false);
  const firstVisitRef = useRef(stored == null);

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(location));
    } catch { /* quota */ }
  }, [location]);

  // Heal stale stored locations whose `name` was saved as raw coordinates
  // (legacy bug where Open-Meteo's reverse-geocoder returned 0 results for
  // most Indian coordinates). Re-fetch a real place name on first paint.
  useEffect(() => {
    if (!location?.latitude || !location?.longitude) return;
    const looksLikeCoords =
      typeof location.name === "string" && COORD_NAME_RE.test(location.name.trim());
    if (!looksLikeCoords) return;

    let cancelled = false;
    reverseGeocode(location.latitude, location.longitude)
      .then((r) => {
        if (cancelled || !r?.name) return;
        if (COORD_NAME_RE.test(r.name)) return; // backend itself fell back — stop
        setLocation((prev) => ({
          ...prev,
          name: r.name,
          admin1: r.admin1 || prev.admin1 || null,
          country: r.country || prev.country || null,
          timezone: r.timezone || prev.timezone || null,
        }));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location?.latitude, location?.longitude, location?.name]);

  // Geolocation behaviour:
  //   • First-time visitor → prompt once. If granted, set their coords and
  //     reverse-geocode for a friendly name. If denied, surface a small
  //     "Tap GPS" hint for ~6s and fall back to the last-saved location.
  //   • Returning visitor whose browser already remembers a "granted"
  //     permission → silently re-detect on every load, so the app always
  //     opens at the user's *current* city even if they've moved/travelled.
  //     We never re-prompt — granted-once is granted-forever for them.
  //   • Returning visitor who previously denied → respect that, never auto
  //     prompt again, just load the stored location.
  useEffect(() => {
    if (!navigator.geolocation) return;

    let cancelled = false;

    const fetchPosition = (silent) => {
      // Mark "asked" immediately so a refresh during the dialog doesn't re-prompt
      try {
        localStorage.setItem(LS_GEO_ASKED, String(Date.now()));
      } catch { /* noop */ }

      navigator.geolocation.getCurrentPosition(
        async (pos) => {
          if (cancelled) return;
          const { latitude, longitude } = pos.coords;
          // Optimistically set coords — the verdict + forecast can render right
          // away — then enrich with city name via reverse geocode.
          setLocation((prev) => ({
            ...prev,
            latitude,
            longitude,
            name: silent ? prev?.name || "Locating…" : "Locating…",
          }));

          // Safety timeout: if reverseGeocode hasn't resolved in 5s, flip to a
          // friendly "Current location" label so the user is never stuck.
          const fallback = setTimeout(() => {
            if (cancelled) return;
            setLocation((prev) =>
              prev?.name === "Locating…"
                ? { ...prev, name: "Current location" }
                : prev,
            );
          }, 5000);

          try {
            const r = await reverseGeocode(latitude, longitude);
            clearTimeout(fallback);
            if (cancelled) return;
            setLocation({
              latitude,
              longitude,
              name: r?.name || "Current location",
              admin1: r?.admin1 || null,
              country: r?.country || null,
              timezone: r?.timezone || null,
            });
          } catch {
            clearTimeout(fallback);
            if (!cancelled) {
              setLocation((prev) => ({
                latitude,
                longitude,
                name: prev?.name && prev.name !== "Locating…" ? prev.name : "Current location",
              }));
            }
          }
        },
        () => {
          // Denied / blocked / timeout — only show hint on first-visit attempts
          if (cancelled || silent) return;
          setShowGpsHint(true);
          setTimeout(() => !cancelled && setShowGpsHint(false), 6000);
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60_000 },
      );
    };

    // Branch 1: first visit → prompt
    if (firstVisitRef.current) {
      fetchPosition(false);
      return () => {
        cancelled = true;
      };
    }

    // Branch 2: returning visitor with already-granted permission → silent re-detect.
    // We only call permissions.query() here because doing it on first visit
    // would be redundant (we're going to prompt anyway).
    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          if (cancelled) return;
          if (status.state === "granted") fetchPosition(true);
        })
        .catch(() => { /* permissions API not supported — skip */ });
    }

    return () => {
      cancelled = true;
    };
  }, []);

  const { confidence } = useRainConfidence(location);
  const { settings } = useSettings();
  const { forecast, summary, quotaError, status, refresh } = useLiveFeed(
    location,
    confidence,
    settings.language,
  );
  // Phase 42 — When the user changes language in Settings, trigger an
  // immediate forecast refresh so the Gemini summary regenerates in the
  // new language right away (instead of waiting for the next polling tick).
  const langOnMountRef = useRef(settings.language);
  useEffect(() => {
    if (langOnMountRef.current !== settings.language) {
      langOnMountRef.current = settings.language;
      refresh();
    }
  }, [settings.language, refresh]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Bumped on every manual refresh — passed down to ExtremesPage so its
  // internal `cache` state can invalidate and refetch (otherwise the
  // masthead button only re-pulled forecast/confidence/summary, leaving
  // Page 4 stale until the user navigated away and back).
  const [refreshNonce, setRefreshNonce] = useState(0);
  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    vibrate(HAPTICS.tap);
    setRefreshNonce((n) => n + 1);
    try {
      await refresh();
    } finally {
      // Keep the spin animation visible for at least 600ms so the user
      // gets feedback even when the cache hits and the request finishes
      // in 30 ms.
      setTimeout(() => setIsRefreshing(false), 600);
    }
  }, [isRefreshing, refresh]);
  const theme = getTheme(forecast);

  // Sync <meta name="theme-color"> so the browser chrome matches the scene
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme.statusBar);
  }, [theme.statusBar]);

  const [emblaRef, emblaApi] = useEmblaCarousel({
    startIndex: 0,
    loop: false,
    align: "start",
    containScroll: "trimSnaps",
    dragFree: false,
  });

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setActivePage(emblaApi.selectedScrollSnap());
      // Subtle haptic so the user "feels" the page change on Android PWA
      vibrate(HAPTICS.TICK);
    };
    emblaApi.on("select", onSelect);
    // Initial set without haptic
    setActivePage(emblaApi.selectedScrollSnap());
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  const scrollTo = useCallback(
    (idx) => emblaApi?.scrollTo(idx),
    [emblaApi],
  );

  // When a city card is tapped on Page 3, update location + jump to Page 1
  const selectCityAndReturn = useCallback(
    (loc) => {
      setLocation(loc);
      scrollTo(0);
    },
    [scrollTo],
  );

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{
        background: "linear-gradient(135deg, #fce7f3 0%, #e0e7ff 45%, #ddd6fe 100%)",
      }}
      data-testid="app-shell"
    >
      {/* The phone frame — max 440px wide, 100dvh tall, soft shadow */}
      <div
        className="relative w-full h-[100dvh] sm:h-[min(100dvh,900px)] sm:max-w-[440px] sm:rounded-[36px] overflow-hidden shadow-2xl"
        style={{
          background: theme.gradient,
          boxShadow: "0 30px 80px rgba(15,23,42,0.25)",
          transition: "background 0.8s ease",
        }}
      >
        {/* Ambient scene glow */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: theme.sceneBg, mixBlendMode: "normal" }}
        />

        {/* Brand mark — persistent across all 4 pages, top-left */}
        <div
          className="absolute z-20 left-4 flex items-center gap-1.5 pointer-events-none select-none"
          style={{ top: "max(28px, calc(env(safe-area-inset-top) + 10px))" }}
          data-testid="brand-mark"
        >
          <div
            className="flex items-center justify-center rounded-lg shrink-0"
            style={{
              width: 28,
              height: 28,
              background: theme.accent,
              boxShadow: `0 4px 12px ${theme.accent}55`,
            }}
          >
            <svg width="15" height="15" viewBox="0 0 12 12" fill="none">
              <path
                d="M7 1 L3 6.5 L5.5 6.5 L4 11 L9 5 L6.5 5 L8 1 Z"
                fill={
                  theme.id === "night" || theme.id === "storm"
                    ? "#0f172a"
                    : "#0f172a"
                }
              />
            </svg>
          </div>
          <div className="leading-none">
            <div
              className="font-display font-bold tracking-tight"
              style={{
                fontSize: 16,
                letterSpacing: "-0.02em",
                color: theme.textPrimary,
              }}
            >
              Nowcast
            </div>
            <div
              className="font-medium"
              style={{
                fontSize: 9.5,
                marginTop: 2,
                letterSpacing: "0.16em",
                color: theme.textMuted,
                textTransform: "uppercase",
              }}
            >
              by Cosmicflow
            </div>
          </div>
        </div>

        {/* Install prompt + status badge floating top-right */}
        <div
          className="absolute z-20 right-4 flex items-center gap-2"
          style={{ top: "max(28px, calc(env(safe-area-inset-top) + 10px))" }}
        >
          <InstallPwaPrompt />
          <AccountChip theme={theme} location={location} />
          <button
            type="button"
            onClick={() => setShowSettings(true)}
            data-testid="settings-button"
            aria-label="Open settings"
            className="flex items-center justify-center rounded-full transition-all active:scale-95"
            style={{
              width: 30,
              height: 30,
              background: theme.chip,
              border: `1px solid ${theme.surfaceBorder}`,
              color: theme.textSecondary,
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <GearSix size={14} weight="bold" />
          </button>
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isRefreshing}
            data-testid="refresh-button"
            aria-label="Refresh forecast"
            className="flex items-center justify-center rounded-full transition-all active:scale-95 disabled:opacity-60"
            style={{
              width: 30,
              height: 30,
              background: theme.chip,
              border: `1px solid ${theme.surfaceBorder}`,
              color: theme.textSecondary,
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
            }}
          >
            <ArrowsClockwise
              size={14}
              weight="bold"
              className={isRefreshing ? "animate-spin" : ""}
            />
          </button>
          <StatusDot status={status} theme={theme} />
        </div>

        {/* Quota banner if upstream is rate-limited */}
        {quotaError && (
          <div
            className="absolute top-0 inset-x-0 z-30 px-4 py-1.5 text-[11px] font-medium text-center"
            style={{
              background: "rgba(251,191,36,0.95)",
              color: "#0f172a",
              paddingTop: "calc(env(safe-area-inset-top) + 6px)",
            }}
            data-testid="quota-banner"
          >
            Upstream weather quota exhausted · radar + extremes still live
          </div>
        )}

        {/* Transient GPS hint shown when geolocation was denied on first visit */}
        <AnimatePresence>
          {showGpsHint && (
            <motion.button
              type="button"
              onClick={() => setShowGpsHint(false)}
              className="absolute z-30 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg"
              style={{
                top: "calc(env(safe-area-inset-top) + 64px)",
                background: theme.surface,
                border: `1px solid ${theme.surfaceBorder}`,
                backdropFilter: "blur(14px)",
                WebkitBackdropFilter: "blur(14px)",
              }}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              data-testid="gps-hint"
            >
              <NavigationArrow size={12} weight="fill" style={{ color: theme.accent }} />
              <span className="text-[11px] font-semibold" style={{ color: theme.textPrimary }}>
                Tap the location pill to use your GPS
              </span>
              <X size={10} weight="bold" style={{ color: theme.textMuted }} />
            </motion.button>
          )}
        </AnimatePresence>

        {/* Embla carousel */}
        <div
          className="h-full w-full overflow-hidden"
          ref={emblaRef}
          data-testid="embla-viewport"
          style={{ paddingTop: "max(72px, calc(env(safe-area-inset-top) + 52px))" }}
        >
          <div className="flex h-full">
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-0">
              <VerdictPage
                location={location}
                onSelectLocation={setLocation}
                forecast={forecast}
                confidence={confidence}
                summary={summary}
                theme={theme}
                status={status}
                onRetry={handleRefresh}
              />
            </div>
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-1">
              <ProGate
                theme={theme}
                feature="Atmospheric Pulse"
                sub="6 illustrated panels — moisture, storm fuel, wind shear, METAR — driven by the same engine the pros trust."
              >
                <PulsePage
                  confidence={confidence}
                  forecast={forecast}
                  location={location}
                  theme={theme}
                  active={activePage === 1}
                />
              </ProGate>
            </div>
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-2">
              <NearbyRainPage
                location={location}
                onSelectLocation={setLocation}
                forecast={forecast}
                confidence={confidence}
                theme={theme}
                active={activePage === 2}
              />
            </div>
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-3">
              <ProGate
                theme={theme}
                feature="Full meteorologist Report"
                sub="CAPE, lifted index, METAR, sounding, 7-day outlook with model consensus — the technical depth pros pay for."
              >
                <ReportPage
                  location={location}
                  onSelectLocation={setLocation}
                  forecast={forecast}
                  confidence={confidence}
                  theme={theme}
                  active={activePage === 3}
                />
              </ProGate>
            </div>
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-4">
              <ExtremesPage
                theme={theme}
                onSelectCity={selectCityAndReturn}
                refreshNonce={refreshNonce}
                location={location}
              />
            </div>
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-5">
              <ProGate
                theme={theme}
                feature="Flood Risk"
                sub="River discharge for India's major rivers — GloFAS-driven 7-day flood watch with ensemble bands. Built for monsoon season."
              >
                <FloodRiskPage
                  location={location}
                  onSelectLocation={setLocation}
                  theme={theme}
                />
              </ProGate>
            </div>
            <div className="flex-[0_0_100%] min-w-0 h-full" data-testid="slide-6">
              <ProGate
                theme={theme}
                feature="Seasonal Forecast"
                sub="The 7-month outlook from the world's best long-range models — temperature & rainfall departures vs the 30-year normal."
              >
                <TodayPage
                  location={location}
                  onSelectLocation={setLocation}
                  forecast={forecast}
                  confidence={confidence}
                  theme={theme}
                />
              </ProGate>
            </div>
          </div>
        </div>

        {/* Page indicator — labeled segmented bar with first-visit swipe hint */}
        <SwipePager
          activePage={activePage}
          onScrollTo={scrollTo}
          theme={theme}
        />

        <SettingsSheet
          open={showSettings}
          onClose={() => setShowSettings(false)}
          theme={theme}
        />

        <TrialExpiredBanner theme={theme} />
      </div>
    </div>
  );
}

function StatusDot({ status, theme }) {
  const color =
    status === "live"
      ? "#10b981"
      : status === "idle" || status === "paused"
        ? theme.textMuted
        : theme.accent;
  const pulse = status === "live" || status === "connecting";
  return (
    <div
      className="flex items-center gap-1 px-2 py-1 rounded-full"
      style={{
        background: theme.chip,
        border: `1px solid ${theme.surfaceBorder}`,
        color: theme.textMuted,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
      data-testid={`status-${status || "unknown"}`}
    >
      <motion.span
        className="inline-block w-1.5 h-1.5 rounded-full"
        style={{ background: color }}
        animate={pulse ? { opacity: [0.5, 1, 0.5] } : undefined}
        transition={pulse ? { duration: 1.8, repeat: Infinity, ease: "easeInOut" } : undefined}
      />
      <span className="text-[10px] uppercase tracking-widest font-medium">
        {status === "live" ? "Live" : status === "idle" ? "Idle" : status === "paused" ? "Paused" : "…"}
      </span>
    </div>
  );
}
